//Imports
import AccountStatus from './account-status.ts';
import BillingCredits from './billing-credits.ts';
import Flex from './flex.ts';
import OnDemand from './on-demand.ts';
import { getTimeSince, sevenDays } from '../helpers/time.js';
import { PRICING_MODEL } from '../constants/billing.ts';

import { ListAllowedActionsResponseAction } from '@clients/cloud-billing';

import type {
  Billing20201105BillingAccount,
  Billing20201105Contract,
  Billing20201105EntitlementBillingMethodDetails,
  Billing20201105FlexibleConsumptionBillingMethodDetails,
  GetPricingModelTransitionsResponseTransition,
  Billing20201105OnDemandBillingMethodDetails,
  ListAllowedActionsResponseAction as ListAllowedActionsResponseActionType,
} from '@clients/cloud-billing';

//Types
export interface BannerMetadata {
  cutoffDate?: Date;
  dueDate?: Date;
  shouldDisplay?: boolean;
  topic: string;
}

export interface BillingFCP {
  upcomingContract?: Billing20201105Contract;
  latestContract?: Billing20201105Contract;
  hasExpiredContract: boolean;
}

export default class Billing {
  #actions?: ListAllowedActionsResponseActionType[];
  #banners?: BannerMetadata[] | null;
  #billingAccount?: Billing20201105BillingAccount | null;
  #onDemandBillingMethodDetails?: Billing20201105OnDemandBillingMethodDetails | null;
  #entitlementBillingMethodDetails?: Billing20201105EntitlementBillingMethodDetails | null;
  #flexibleConsumptionBillingMethodDetails?: Billing20201105FlexibleConsumptionBillingMethodDetails | null;
  #fcp?: BillingFCP | null;
  #transitions?: GetPricingModelTransitionsResponseTransition[] | null;

  constructor({
    actions,
    banners,
    billingAccount,
    onDemandBillingMethodDetails,
    entitlementBillingMethodDetails,
    flexibleConsumptionBillingMethodDetails,
    fcp,
    transitions,
  }: {
    actions?: ListAllowedActionsResponseActionType[];
    banners?: BannerMetadata[];
    billingAccount?: Billing20201105BillingAccount;
    onDemandBillingMethodDetails?: Billing20201105OnDemandBillingMethodDetails;
    entitlementBillingMethodDetails?: Billing20201105EntitlementBillingMethodDetails;
    flexibleConsumptionBillingMethodDetails?: Billing20201105FlexibleConsumptionBillingMethodDetails;
    fcp?: BillingFCP;
    transitions?: GetPricingModelTransitionsResponseTransition[];
  } = {}) {
    this.#actions = actions;
    this.#banners = banners;
    this.#billingAccount = billingAccount;
    this.#onDemandBillingMethodDetails = onDemandBillingMethodDetails;
    this.#entitlementBillingMethodDetails = entitlementBillingMethodDetails;
    this.#flexibleConsumptionBillingMethodDetails =
      flexibleConsumptionBillingMethodDetails;
    this.#fcp = fcp;
    this.#transitions = transitions;
  }

  get billingAccount() {
    return this.#billingAccount ?? null;
  }

  //Actions
  get actions() {
    return this.#actions ?? null;
  }

  get banners() {
    // TODO: Replace this hard coded response with the dynamic
    // response from the backend once the listInAppNotifications
    // endpoint is returning the expected banner
    return [
      {
        topic: 'PAYG_UPCOMING_CUTOFF',
        dueDate: new Date(),
        cutoffDate: new Date(),
        // The'shouldDisplay' field is only being
        // used while the banners getter is hard coded.
        // Whether or not the banner should display for
        // the org should eventually be determined in the
        // BE code, with the pricing model taken into account.
        shouldDisplay: this.hasOnDemandPayment,
      },
    ];
  }

  //Allowed Actions Getters
  /** Determines if the account can create a resource */
  get canCreateResource() {
    return (
      this.actions?.includes(ListAllowedActionsResponseAction.CREATE) ?? false
    );
  }

  /** Determines if the account can use a resource */
  get canUseResource() {
    return (
      this.actions?.includes(ListAllowedActionsResponseAction.USE) ?? false
    );
  }

  // Account Status
  get status() {
    return new AccountStatus(this.#billingAccount?.status ?? null);
  }

  // Credits
  get credits() {
    // estimatedRemainingCredits is an empty string if not OnDemand or Trial account
    return new BillingCredits(
      this.billingAccount?.estimatedRemainingCredits,
      this.billingAccount?.createdAt,
    );
  }

  get onDemand() {
    return new OnDemand(
      this.#billingAccount,
      this.#onDemandBillingMethodDetails ?? null,
    );
  }

  //Pricing Model Getters
  get pricingModel() {
    return this.billingAccount?.pricingModel ?? null;
  }

  get pricingModelTransitions() {
    return this.#transitions ?? null;
  }

  get hasFlexPayment() {
    return this.pricingModel === PRICING_MODEL.FLEX;
  }

  /** Will return true for both Trial and On Demand accounts */
  get hasTrialOrOnDemandPayment() {
    return this.pricingModel === PRICING_MODEL.PAYG;
  }

  /**
   * Both Trial and On Demand accounts will return PRICING_MODEL_PAYG,
   * so we also need to check that the On Demand status is `UNSET'
   * (i.e. no credit card is on file) to confirm that it is a Trial account.
   */
  get hasTrialPayment() {
    return (
      this.pricingModel === PRICING_MODEL.PAYG &&
      this.onDemand?.status?.isOnDemandUnset
    );
  }

  /**
   * Both Trial and On Demand accounts will return PRICING_MODEL_PAYG
   * so we also need to check that the On Demand status is not `UNSET`
   * to confirm that it is not a Trial account
   */
  get hasOnDemandPayment() {
    return (
      this.pricingModel === PRICING_MODEL.PAYG &&
      !this.onDemand?.status.isOnDemandUnset
    );
  }

  /** Contract here refers to an Entitlement contract */
  get hasContractPayment() {
    return this.pricingModel === PRICING_MODEL.ENTITLEMENT;
  }

  //Entitlement Contract Details
  get entitlementContractDetails(): Billing20201105EntitlementBillingMethodDetails | null {
    return this.hasContractPayment
      ? (this.#entitlementBillingMethodDetails ?? null)
      : null;
  }

  //Flex
  get flex() {
    return (
      new Flex(
        this.#fcp ?? null,
        this.#flexibleConsumptionBillingMethodDetails ?? null,
        this.hasFlexPayment,
      ) || null
    );
  }

  /**
   * Returns true if there was a recent downgrade from FLEX to PAYG
   */
  get recentDowngradeFlexToPAYG() {
    const transitions = this.pricingModelTransitions;
    const length = transitions ? transitions.length : 0;
    if (length <= 1) return false;

    const timeSinceTransition = getTimeSince(
      transitions ? transitions[length - 2]?.end : undefined,
    ).milliseconds;

    return !!(
      transitions &&
      timeSinceTransition !== undefined &&
      transitions[length - 2]?.pricingModel === PRICING_MODEL.FLEX &&
      transitions[length - 1]?.pricingModel === PRICING_MODEL.PAYG &&
      timeSinceTransition <= sevenDays
    );
  }
}
